/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState } from 'react';
import { X, ChevronRight, ChevronLeft, Server, Zap, Cloud, DollarSign, Check, Loader } from 'lucide-react';
import type { EnvironmentType, ResourceTemplate, CreateDeploymentRequest } from '../../lib/api';
import { getEnvironmentTypeLabel } from '../../lib/api/deployments';

interface DeploymentWizardProps {
  productId: string;
  productName: string;
  templates: ResourceTemplate[];
  isCreating: boolean;
  onClose: () => void;
  onCreate: (request: CreateDeploymentRequest) => void;
}

type WizardStep = 'environment' | 'configuration' | 'domain' | 'review';

const ENVIRONMENT_TYPES: { type: EnvironmentType; description: string; icon: React.ReactNode }[] = [
  { 
    type: 'dev', 
    description: 'Development environment for active development work',
    icon: <Server size={24} style={{ color: '#6b7280' }} />
  },
  { 
    type: 'test', 
    description: 'Testing environment for QA and automated tests',
    icon: <Zap size={24} style={{ color: '#3b82f6' }} />
  },
  { 
    type: 'staging', 
    description: 'Pre-production environment that mirrors production',
    icon: <Cloud size={24} style={{ color: '#8b5cf6' }} />
  },
  { 
    type: 'production', 
    description: 'Live production environment for end users',
    icon: <Cloud size={24} style={{ color: '#22c55e' }} />
  },
  { 
    type: 'demo', 
    description: 'Demo environment for sales and demonstrations',
    icon: <DollarSign size={24} style={{ color: '#f59e0b' }} />
  },
];

export function DeploymentWizard({
  productId,
  productName,
  templates,
  isCreating,
  onClose,
  onCreate,
}: DeploymentWizardProps) {
  const [currentStep, setCurrentStep] = useState<WizardStep>('environment');
  const [environmentType, setEnvironmentType] = useState<EnvironmentType | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<ResourceTemplate | null>(null);
  const [customDomain, setCustomDomain] = useState('');
  const [useCustomDomain, setUseCustomDomain] = useState(false);

  const steps: WizardStep[] = ['environment', 'configuration', 'domain', 'review'];
  const currentStepIndex = steps.indexOf(currentStep);

  const canProceed = () => {
    switch (currentStep) {
      case 'environment':
        return environmentType !== null;
      case 'configuration':
        return selectedTemplate !== null;
      case 'domain':
        return !useCustomDomain || customDomain.trim().length > 0;
      case 'review':
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep === 'review') {
      handleCreate();
    } else {
      const nextIndex = currentStepIndex + 1;
      if (nextIndex < steps.length) {
        setCurrentStep(steps[nextIndex]);
      }
    }
  };

  const handleBack = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(steps[prevIndex]);
    }
  };

  const handleCreate = () => {
    if (!environmentType || !selectedTemplate) return;

    const request: CreateDeploymentRequest = {
      productId,
      environmentType,
      templateId: selectedTemplate.id,
      customDomain: useCustomDomain && customDomain.trim() ? customDomain.trim() : undefined,
    };

    onCreate(request);
  };

  const getGeneratedDomain = () => {
    if (!environmentType) return '';
    const productSlug = productName.toLowerCase().replace(/\s+/g, '-');
    return `${productSlug}-${environmentType}.velocityhosted.com`;
  };

  const getRecommendedTemplate = () => {
    if (!environmentType || templates.length === 0) return null;
    return templates.find(t => t.recommended.includes(environmentType)) || templates[0];
  };

  // Auto-select recommended template when environment changes
  const handleEnvironmentSelect = (type: EnvironmentType) => {
    setEnvironmentType(type);
    const recommended = templates.find(t => t.recommended.includes(type));
    if (recommended) {
      setSelectedTemplate(recommended);
    }
  };

  const renderStepIndicator = () => (
    <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '32px' }}>
      {steps.map((step, index) => (
        <div
          key={step}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <div
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '14px',
              fontWeight: 500,
              backgroundColor: index <= currentStepIndex ? '#0066cc' : '#e5e7eb',
              color: index <= currentStepIndex ? 'white' : '#9ca3af',
              transition: 'all 0.2s',
            }}
          >
            {index < currentStepIndex ? <Check size={16} /> : index + 1}
          </div>
          {index < steps.length - 1 && (
            <div
              style={{
                width: '40px',
                height: '2px',
                backgroundColor: index < currentStepIndex ? '#0066cc' : '#e5e7eb',
                transition: 'background-color 0.2s',
              }}
            />
          )}
        </div>
      ))}
    </div>
  );

  const renderEnvironmentStep = () => (
    <div>
      <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: 600, color: '#111827' }}>
        Select Environment Type
      </h3>
      <p style={{ margin: '0 0 24px 0', fontSize: '14px', color: '#6b7280' }}>
        Choose the type of environment you want to create for {productName}
      </p>
      
      <div style={{ display: 'grid', gap: '12px' }}>
        {ENVIRONMENT_TYPES.map(({ type, description, icon }) => (
          <div
            key={type}
            onClick={() => handleEnvironmentSelect(type)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              padding: '16px',
              border: `2px solid ${environmentType === type ? '#0066cc' : '#e5e7eb'}`,
              borderRadius: '12px',
              cursor: 'pointer',
              backgroundColor: environmentType === type ? '#eff6ff' : 'white',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              if (environmentType !== type) {
                e.currentTarget.style.borderColor = '#d1d5db';
                e.currentTarget.style.backgroundColor = '#f9fafb';
              }
            }}
            onMouseLeave={(e) => {
              if (environmentType !== type) {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.backgroundColor = 'white';
              }
            }}
          >
            {icon}
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 500, color: '#111827', marginBottom: '2px' }}>
                {getEnvironmentTypeLabel(type)}
              </div>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>{description}</div>
            </div>
            {environmentType === type && (
              <div style={{ 
                width: '24px', 
                height: '24px', 
                borderRadius: '50%', 
                backgroundColor: '#0066cc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Check size={14} color="white" />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderConfigurationStep = () => (
    <div>
      <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: 600, color: '#111827' }}>
        Choose Configuration
      </h3>
      <p style={{ margin: '0 0 24px 0', fontSize: '14px', color: '#6b7280' }}>
        Select a resource template for your {getEnvironmentTypeLabel(environmentType!).toLowerCase()} environment
      </p>
      
      <div style={{ display: 'grid', gap: '12px' }}>
        {templates.map((template) => {
          const isRecommended = environmentType && template.recommended.includes(environmentType);
          const isSelected = selectedTemplate?.id === template.id;
          
          return (
            <div
              key={template.id}
              onClick={() => setSelectedTemplate(template)}
              style={{
                position: 'relative',
                padding: '16px',
                border: `2px solid ${isSelected ? '#0066cc' : '#e5e7eb'}`,
                borderRadius: '12px',
                cursor: 'pointer',
                backgroundColor: isSelected ? '#eff6ff' : 'white',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = '#d1d5db';
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.backgroundColor = 'white';
                }
              }}
            >
              {isRecommended && (
                <span style={{
                  position: 'absolute',
                  top: '-10px',
                  right: '12px',
                  padding: '2px 8px',
                  fontSize: '11px',
                  fontWeight: 500,
                  backgroundColor: '#22c55e',
                  color: 'white',
                  borderRadius: '4px',
                }}>
                  Recommended
                </span>
              )}
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontWeight: 500, color: '#111827', marginBottom: '4px' }}>
                    {template.name}
                  </div>
                  <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>
                    {template.machineType} • {template.diskSizeGb}GB SSD
                    {template.preemptible && ' • Preemptible'}
                  </div>
                </div>
                
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '18px', fontWeight: 600, color: '#111827' }}>
                    ~${template.estimatedMonthlyCost}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>per month</div>
                </div>
              </div>
              
              {isSelected && (
                <div style={{ 
                  position: 'absolute',
                  top: '16px',
                  left: '16px',
                  width: '24px', 
                  height: '24px', 
                  borderRadius: '50%', 
                  backgroundColor: '#0066cc',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Check size={14} color="white" />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderDomainStep = () => (
    <div>
      <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: 600, color: '#111827' }}>
        Configure Domain
      </h3>
      <p style={{ margin: '0 0 24px 0', fontSize: '14px', color: '#6b7280' }}>
        Choose how you want to access your deployment
      </p>
      
      <div style={{ display: 'grid', gap: '12px' }}>
        <div
          onClick={() => setUseCustomDomain(false)}
          style={{
            padding: '16px',
            border: `2px solid ${!useCustomDomain ? '#0066cc' : '#e5e7eb'}`,
            borderRadius: '12px',
            cursor: 'pointer',
            backgroundColor: !useCustomDomain ? '#eff6ff' : 'white',
            transition: 'all 0.2s',
          }}
        >
          <div style={{ fontWeight: 500, color: '#111827', marginBottom: '4px' }}>
            Auto-generated subdomain
          </div>
          <div style={{ fontSize: '14px', color: '#0066cc', fontFamily: 'monospace' }}>
            {getGeneratedDomain()}
          </div>
        </div>
        
        <div
          onClick={() => setUseCustomDomain(true)}
          style={{
            padding: '16px',
            border: `2px solid ${useCustomDomain ? '#0066cc' : '#e5e7eb'}`,
            borderRadius: '12px',
            cursor: 'pointer',
            backgroundColor: useCustomDomain ? '#eff6ff' : 'white',
            transition: 'all 0.2s',
          }}
        >
          <div style={{ fontWeight: 500, color: '#111827', marginBottom: '8px' }}>
            Custom domain
          </div>
          {useCustomDomain && (
            <input
              type="text"
              placeholder="app.yourdomain.com"
              value={customDomain}
              onChange={(e) => setCustomDomain(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                fontFamily: 'monospace',
                outline: 'none',
              }}
              onFocus={(e) => e.target.style.borderColor = '#0066cc'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            />
          )}
          {useCustomDomain && (
            <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#6b7280' }}>
              You'll need to configure DNS after deployment. Instructions will be provided.
            </p>
          )}
        </div>
      </div>
    </div>
  );

  const renderReviewStep = () => (
    <div>
      <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: 600, color: '#111827' }}>
        Review & Deploy
      </h3>
      <p style={{ margin: '0 0 24px 0', fontSize: '14px', color: '#6b7280' }}>
        Confirm your deployment configuration
      </p>
      
      <div style={{ 
        backgroundColor: '#f9fafb', 
        borderRadius: '12px', 
        padding: '20px',
        border: '1px solid #e5e7eb'
      }}>
        <div style={{ display: 'grid', gap: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#6b7280' }}>Product</span>
            <span style={{ fontWeight: 500, color: '#111827' }}>{productName}</span>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#6b7280' }}>Environment</span>
            <span style={{ fontWeight: 500, color: '#111827' }}>
              {environmentType && getEnvironmentTypeLabel(environmentType)}
            </span>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#6b7280' }}>Configuration</span>
            <span style={{ fontWeight: 500, color: '#111827' }}>
              {selectedTemplate?.name}
            </span>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#6b7280' }}>Machine</span>
            <span style={{ fontWeight: 500, color: '#111827' }}>
              {selectedTemplate?.machineType} • {selectedTemplate?.diskSizeGb}GB
            </span>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#6b7280' }}>Domain</span>
            <span style={{ fontWeight: 500, color: '#111827', fontFamily: 'monospace' }}>
              {useCustomDomain && customDomain ? customDomain : getGeneratedDomain()}
            </span>
          </div>
          
          <div style={{ 
            borderTop: '1px solid #e5e7eb', 
            paddingTop: '16px', 
            display: 'flex', 
            justifyContent: 'space-between' 
          }}>
            <span style={{ fontWeight: 500, color: '#111827' }}>Estimated Cost</span>
            <span style={{ fontSize: '18px', fontWeight: 600, color: '#0066cc' }}>
              ~${selectedTemplate?.estimatedMonthlyCost}/month
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div 
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          width: '100%',
          maxWidth: '560px',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          padding: '20px 24px',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>
            New Deployment
          </h2>
          <button
            onClick={onClose}
            style={{
              padding: '8px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              color: '#6b7280',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px', flex: 1, overflowY: 'auto' }}>
          {renderStepIndicator()}
          
          {currentStep === 'environment' && renderEnvironmentStep()}
          {currentStep === 'configuration' && renderConfigurationStep()}
          {currentStep === 'domain' && renderDomainStep()}
          {currentStep === 'review' && renderReviewStep()}
        </div>

        {/* Footer */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          padding: '16px 24px',
          borderTop: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb'
        }}>
          <button
            onClick={handleBack}
            disabled={currentStepIndex === 0}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '10px 20px',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px',
              backgroundColor: 'white',
              color: currentStepIndex === 0 ? '#9ca3af' : '#374151',
              cursor: currentStepIndex === 0 ? 'not-allowed' : 'pointer',
            }}
          >
            <ChevronLeft size={16} />
            Back
          </button>
          
          <button
            onClick={handleNext}
            disabled={!canProceed() || isCreating}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '10px 24px',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 500,
              backgroundColor: canProceed() && !isCreating ? '#0066cc' : '#e5e7eb',
              color: canProceed() && !isCreating ? 'white' : '#9ca3af',
              cursor: canProceed() && !isCreating ? 'pointer' : 'not-allowed',
            }}
          >
            {isCreating ? (
              <>
                <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                Creating...
              </>
            ) : currentStep === 'review' ? (
              <>
                Deploy
                <ChevronRight size={16} />
              </>
            ) : (
              <>
                Continue
                <ChevronRight size={16} />
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

